import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, ExternalLink, Mail, MapPin, Package, ShoppingBag } from 'lucide-react';
import { Button } from '../components/ui/button';
import SocialShare from '../components/SocialShare';
import { useLocalizedNews } from '../data/useLocalizedNews';
import { useTranslation } from 'react-i18next';

import i18n from '../i18n';
const t = (k, o) => i18n.t(k, o);

export const NewsArticle = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const article = useLocalizedNews().bySlug(slug);
  const showNorwegianOnly = article && !i18n.language.startsWith('no') && !article.isTranslated;

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="news-not-found">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">{t('pg.NewsArticle.41ca27')}</h1>
          <Link to="/">
            <Button className="bg-green-500 text-black font-bold">
              {t('pg.NewsArticle.8560fc')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="news-article-page" className="min-h-screen">
      {/* Hero */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-500 hover:text-green-500 transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('common.backToHome')}
          </Link>
          
          {showNorwegianOnly && (
            <p className="mb-6 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-2" data-testid="news-norwegian-only">{t('common.norwegianOnly')}</p>
          )}
          <div className="mb-6">
            <span className="bg-green-500 text-black text-xs font-bold px-3 py-1 rounded uppercase">
              {article.category}
            </span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tighter text-slate-900 mb-6">
            {article.title}
          </h1>
          
          <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
            <Calendar className="w-4 h-4" />
            {article.date}
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="px-4 sm:px-6 lg:px-8 mb-12">
        <div className="max-w-4xl mx-auto">
          {article.imageStyle === 'cover' ? (
            <div className="rounded-2xl overflow-hidden aspect-video">
              <img 
                src={article.image} 
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className="bg-white rounded-2xl p-12 flex items-center justify-center">
              <img 
                src={article.image} 
                alt={article.title}
                className="max-h-48 object-contain"
              />
            </div>
          )}
        </div>
      </section>

      {/* Article Content */}
      <section className="px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-4xl mx-auto">
          <article className="prose prose-invert prose-lg max-w-none">
            <div 
              className="text-slate-600 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
            
            {/* External Links */}
            {article.externalLinks && (
              <div className="bg-white rounded-xl p-6 my-10 border border-slate-200 dark:border-slate-700">
                <h3 className="text-lg font-bold text-slate-900 mb-4">{t('news.readMore')}:</h3>
                <div className="space-y-3">
                  {article.externalLinks.map((link, index) => (
                    <a 
                      key={index}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-green-500 hover:underline"
                    >
                      <ExternalLink className="w-4 h-4 flex-shrink-0" />
                      {link.label}
                    </a>
                  ))}
                </div>
              </div>
            )}
            
            {/* CTA Button */}
            {article.ctaButton && (
              <div className="my-10">
                <a 
                  href={article.ctaButton.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-green-500 text-black font-bold px-8 py-4 rounded-lg hover:bg-green-500/90 transition-colors text-lg"
                >
                  {article.ctaButton.label}
                  <ExternalLink className="w-5 h-5" />
                </a>
              </div>
            )}
            
            {/* Contact Email */}
            {article.contactEmail && (
              <div className="bg-white rounded-xl p-6 my-10 border border-slate-200 dark:border-slate-700">
                <h3 className="text-lg font-bold text-slate-900 mb-3">{t('pg.NewsArticle.8d1c6b')}</h3>
                <a 
                  href={`mailto:${article.contactEmail}`}
                  className="inline-flex items-center gap-2 text-green-500 hover:underline"
                >
                  <Mail className="w-5 h-5" />
                  {article.contactEmail}
                </a>
              </div>
            )}
            
            {/* Packages */}
            {article.packages && (
              <div className="grid sm:grid-cols-2 gap-6 my-10">
                {article.packages.map((pkg, index) => (
                  <div 
                    key={index}
                    className="bg-white rounded-xl p-6 border border-slate-200 hover:border-green-500/50 transition-colors"
                  >
                    <Package className="w-8 h-8 text-green-500 mb-4" />
                    <h3 className="text-xl font-bold text-slate-900 mb-2">{pkg.name}</h3>
                    <p className="text-3xl font-bold text-green-500 mb-2">{pkg.memberPrice}</p>
                    <p className="text-slate-500 text-sm">{pkg.description}</p>
                  </div>
                ))}
              </div>
            )}
            
            {article.additionalInfo && (
              <div 
                className="text-slate-600 dark:text-slate-300"
                dangerouslySetInnerHTML={{ __html: article.additionalInfo }}
              />
            )}

            {/* Image Gallery */}
            {article.gallery && article.gallery.length > 0 && (
              <div className="my-12" data-testid="article-gallery">
                <h3 className="text-2xl font-bold text-slate-900 mb-6">{t('pg.NewsArticle.51d999')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {article.gallery.map((img, index) => (
                    <a
                      key={index}
                      href={img.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`gallery-image-${index}`}
                      className="group relative overflow-hidden rounded-2xl border border-slate-200 hover:border-green-500/50 transition-all duration-300 block"
                    >
                      <div className="aspect-[4/3] bg-slate-100">
                        <img
                          src={img.url}
                          alt={img.alt}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      {img.alt && (
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <p className="text-white text-sm">{img.alt}</p>
                        </div>
                      )}
                    </a>
                  ))}
                </div>
              </div>
            )}
            
            {/* Order Info */}
            {article.orderInfo && (
              <div className="bg-white rounded-2xl p-8 mt-10 border border-slate-200 dark:border-slate-700">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <ShoppingBag className="w-6 h-6 text-green-500" />
                  {t('pg.NewsArticle.92128e')}
                </h2>
                
                <p className="text-slate-600 mb-4">{t('pg.NewsArticle.39c5e0')}</p>
                
                <a 
                  href={`mailto:${article.orderInfo.email}`}
                  className="inline-flex items-center gap-2 bg-green-500 text-black font-bold px-6 py-3 rounded-lg hover:bg-green-500/90 transition-colors mb-8"
                >
                  <Mail className="w-5 h-5" />
                  {article.orderInfo.email}
                </a>
                
                <div className="space-y-6">
                  <div>
                    <p className="text-slate-900 font-bold mb-3">{t('pg.NewsArticle.feac0b')}</p>
                    <ul className="space-y-2">
                      {article.orderInfo.instructions.map((item, index) => (
                        <li key={index} className="flex items-start gap-2 text-slate-600 dark:text-slate-300">
                          <span className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                          {item}
                        </li>
                      ))}
                    </ul>
                    {article.orderInfo.tip && (
                      <p className="text-green-500 text-sm mt-4 italic">{article.orderInfo.tip}</p>
                    )}
                  </div>
                  
                  <div className="border-t border-slate-200 pt-6">
                    <p className="text-slate-900 font-bold mb-3">{t('pg.NewsArticle.cf4d2d')}</p>
                    <ul className="space-y-2">
                      {article.orderInfo.confirmation.map((item, index) => (
                        <li key={index} className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                          <span className="w-1.5 h-1.5 bg-green-500 rounded-full flex-shrink-0"></span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
            
            {/* Comet Sport CTA - only for comet-sport article */}
            {slug === 'comet-sport-2026' && (
              <div className="bg-gradient-to-r from-green-500/20 to-transparent rounded-2xl p-8 mt-12 border border-green-500/30">
                <p className="text-2xl font-bold text-slate-900 mb-4">
                  {t('pg.NewsArticle.0363fa')}
                </p>
                <a 
                  href="https://cometsport.no/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-green-500 font-bold hover:underline"
                >
                  {t('pg.NewsArticle.f09acb')}
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            )}
            
            {/* Social Share */}
            <SocialShare title={article.title} />

            {/* Back to news archive */}
            <div className="mt-12 pt-8 border-t border-slate-200 dark:border-slate-700">
              <Link 
                to="/nyheter"
                className="inline-flex items-center gap-2 text-slate-500 hover:text-green-500 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('pg.NewsArticle.8a83e1')}
              </Link>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
};

export default NewsArticle;
